import React, { useRef, useState } from "react";
import { FileText, Loader2, Upload, X } from "lucide-react";

export default function CompactUploadRow({
  id,
  title,
  multiple = false,
  accept = ".pdf,.jpg,.jpeg,.png",
  button = "Upload",
  existingFiles = [],
  uploadingFiles = [],
  onFiles,
  onRemove,
  maxSizeMB = 10,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const MAX_BYTES = maxSizeMB ? maxSizeMB * 1024 * 1024 : null;
  const hasFiles = existingFiles?.length > 0;
  const isUploading = uploadingFiles?.length > 0;

  const processFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    if (!MAX_BYTES) {
      setError("");
      onFiles?.(files);
      return;
    }

    const valid = files.filter((f) => f.size <= MAX_BYTES);
    if (valid.length < files.length) {
      setError(`Some files exceed ${maxSizeMB}MB and were not added.`);
    } else {
      setError("");
    }
    if (valid.length) onFiles?.(valid);
  };

  const fileLabel = (file) => file.file_name || file.name || "Uploaded file";

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 min-w-0">
      <div className="flex items-start gap-2 min-w-0">
        <FileText className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{title}</p>

          {isUploading ? (
            <div className="mt-1 space-y-1">
              {uploadingFiles.map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  <span className="truncate">{u.name}</span>
                  <span>{u.progress ?? 0}%</span>
                </div>
              ))}
            </div>
          ) : null}

          {hasFiles ? (
            <ul className="mt-1 space-y-1">
              {existingFiles.map((file) => {
                const fid = file.id || file.name || file.file_name;
                return (
                  <li key={fid} className="flex items-center gap-2 text-xs text-slate-600 min-w-0">
                    <span className="truncate flex-1">{fileLabel(file)}</span>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded-md hover:bg-slate-100 text-slate-500"
                      onClick={() => onRemove?.(fid)}
                      aria-label={`Remove ${fileLabel(file)}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : !isUploading ? (
            <p className="text-xs text-slate-500 mt-0.5">Not uploaded</p>
          ) : null}

          {error ? (
            <p className="text-xs text-rose-600 mt-1" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
          aria-label={`${button} for ${title}`}
        >
          <Upload className="h-3.5 w-3.5" aria-hidden="true" />
          {hasFiles ? "Add" : button}
        </button>
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          processFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
